import { getGeographyTheme } from '@shared/geography-theme';

import {
  BROWSER_SUPPORT_MATRIX,
  BROWSE_STATE_OWNERSHIP,
  DESKTOP_NAV_MODEL,
  GEOGRAPHY_FALLBACK_MATRIX,
  GEOGRAPHY_SWITCHER_BEHAVIOR,
  PERFORMANCE_BUDGETS,
  PUBLIC_BOTTOM_NAV,
  PUBLIC_ROUTE_MAP,
  ROOT_ROUTE_DISCOVERY_BEHAVIOR,
} from '@/lib/public-route-config';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const colorTokens = [
  {
    label: 'Background',
    value: 'var(--background)',
    note: 'Warm paper base for editorial surfaces.',
  },
  { label: 'Foreground', value: 'var(--foreground)', note: 'Dark ink for dense reading.' },
  {
    label: 'Primary',
    value: 'var(--primary)',
    note: 'Garnet action color for commitments and navigation.',
  },
  {
    label: 'Accent',
    value: 'var(--accent)',
    note: 'Terracotta highlight for warmth and emphasis.',
  },
  {
    label: 'Muted',
    value: 'var(--muted)',
    note: 'Quiet support surface for chips and section breaks.',
  },
  {
    label: 'Border',
    value: 'var(--border)',
    note: 'Low-contrast scaffolding that avoids SaaS blue chrome.',
  },
];

const typeScale = [
  {
    label: 'Display',
    className: 'section-title',
    sample: 'Local learning, built for the way neighborhoods actually move.',
  },
  {
    label: 'Heading',
    className: 'text-3xl font-semibold',
    sample: 'Regional discovery stays emotional, not transactional.',
  },
  {
    label: 'Body',
    className: 'section-copy',
    sample:
      'Body copy should feel calm, warm, and direct. It can be editorial without becoming vague.',
  },
  { label: 'Label', className: 'editorial-kicker', sample: 'Touch-first navigation' },
];

const motionPrinciples = [
  'Use short fades and vertical slides for page-intent changes like sheets, drawers, and card reveals.',
  'Use longer, softer transitions only for large atmosphere elements such as hero gradients or background glows.',
  'Avoid animating layout shifts on browse filters. Filters should open in overlays on phone instead of pushing content.',
  'Prefer motion that reinforces orientation: sticky bars, bottom CTA surfaces, and restored scroll state should feel stable.',
];

const brandNotes = [
  'Kalovate should read as local, human, and editorial rather than glossy SaaS.',
  'Warm neutrals and garnet/terracotta accents replace the old blue-orange marketplace palette.',
  'Display typography can be expressive, but interaction copy stays clean and highly legible.',
  'Phone-first browsing is a product stance, not a fallback responsive mode.',
];

const geographyStyleDirections = [
  {
    slug: 'hoosier-hills',
    label: 'Hoosier Hills',
    scope: 'Region',
    summary:
      'County-wide browsing should feel like Bloomington inverted: cream-forward, crimson-accented, and calmer than the locality layer while still staying tied to Monroe County’s limestone-and-trail identity.',
    typography:
      'Use a crisp sans-led system with restrained contrast. Region pages should feel infrastructural and wayfinding-first, not institution-first.',
    imagery: [
      'Limestone textures, creek edges, trail maps, and tree canopy',
      'County-wide browse moments should favor orientation over landmark hero shots',
      'Keep region photography broad and connective so locality cards can introduce stronger identity',
    ],
    uiRules: [
      'Use cream as the lead surface color and crimson as the supporting accent, effectively flipping Bloomington’s hierarchy.',
      'Keep surfaces broad, neutral, and quieter than locality-specific pages even when crimson appears.',
      'This is a synthesized browse style for Kalovate, not an official municipal brand.',
    ],
    sources: [],
  },
  {
    slug: 'bloomington',
    label: 'Bloomington',
    scope: 'Locality',
    summary:
      'Bloomington should read as IU-adjacent first: crimson-led, cream-supported, with limestone, brick walks, and a confident academic tone.',
    typography:
      'Headlines should borrow the compressed cadence of Benton Sans Condensed; body copy stays clean and direct like IU’s digital system.',
    imagery: [
      'Sample Gates, Old Crescent limestone buildings, and Kirkwood brick rhythm',
      'Tree canopy, fall color, and student movement rather than generic skyline shots',
      'Blend campus limestone with downtown energy so the city still feels civic, not only collegiate',
    ],
    uiRules: [
      'Crimson is the dominant action color everywhere that matters, with cream and limestone neutrals supporting it.',
      'Corners and labels should feel a little sharper and more structured than the other Monroe County styles.',
      'Avoid treating Bloomington like sports merch; keep the mood academic and civic.',
    ],
    sources: [
      { label: 'IU Rivet design tokens', href: 'https://rivet.iu.edu/design-tokens/' },
      {
        label: 'IU Bloomington visit language',
        href: 'https://lab.indiana.edu/admissions/visit.html',
      },
      {
        label: 'IU Bloomington life in Bloomington',
        href: 'https://admissions.indiana.edu/life/bloomington.html',
      },
    ],
  },
  {
    slug: 'ellettsville',
    label: 'Ellettsville',
    scope: 'Locality',
    summary:
      'Ellettsville should feel civic and proudly Hoosier: Indiana state flag navy and gold ground the palette, while a serif headline voice and neighborly pace distinguish it from Bloomington.',
    typography:
      'Use a serif headline voice with practical sans body copy. The tone should feel civic and grounded — community-first, not collegiate.',
    imagery: [
      'Limestone Heritage Trail gateway, carvings, tools, and sculpture',
      'Historic downtown storefronts, Old Town Hall / History Center, and festival moments',
      'Stone texture, greenway edges, creek crossings, and community-scale gathering scenes',
    ],
    uiRules: [
      'Lead with Indiana state flag navy (#002D6C) as the primary action color and gold (#FFCD00) as the accent.',
      'Photography should emphasize civic closeness and community gathering over landmark shots.',
      'Cards can feel softer and more neighborly than Bloomington, while still grounded and sturdy.',
    ],
    sources: [
      { label: 'Town of Ellettsville home page', href: 'https://ellettsville.in.us/' },
      {
        label: 'Historic Downtown Ellettsville',
        href: 'https://ellettsville.in.us/department/division.php?structureid=50',
      },
      {
        label: 'Visit Bloomington on the Heritage Trail',
        href: 'https://www.visitbloomington.com/blog/stories/post/urban-trails-in-bloomington/',
      },
      {
        label: 'Visit Bloomington day trip to Ellettsville',
        href: 'https://www.visitbloomington.com/blog/stories/post/day-trip-to-elletsville/',
      },
    ],
  },
] as const;

function TokenSwatch({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-[1.5rem] border border-border/70 bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{value}</p>
        </div>
        <div
          className="h-12 w-12 rounded-full border border-white/40 shadow-inner"
          style={{ background: value }}
          aria-hidden
        />
      </div>
      <p className="mt-4 text-sm leading-6 text-muted-foreground">{note}</p>
    </div>
  );
}

function SampleCards() {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">Class card</Badge>
            <Badge variant="outline" className="rounded-full">
              Comfortable
            </Badge>
          </div>
          <CardTitle className="text-2xl">Knife Skills for Weeknight Cooks</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm leading-6 text-muted-foreground">
            Compact metadata, one strong title, and a visible booking action. The card surface
            should feel like a local listing, not a generic product tile.
          </p>
          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span>Bloomington</span>
            <span>•</span>
            <span>Thu, 6:30 PM</span>
            <span>•</span>
            <span>$45</span>
          </div>
          <Button className="touch-target rounded-full px-5">View class</Button>
        </CardContent>
      </Card>

      <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-full bg-accent/15 px-3 py-1 text-accent-foreground">
              Creator card
            </Badge>
            <Badge variant="outline" className="rounded-full">
              Expanded
            </Badge>
          </div>
          <CardTitle className="text-2xl">Nora Fields</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm font-medium text-primary">
            Printmaker • community host • Bloomington
          </p>
          <p className="text-sm leading-6 text-muted-foreground">
            Creator cards should feel calm and trustworthy, with enough voice to suggest what
            learning with this person is actually like.
          </p>
          <Button variant="outline" className="touch-target rounded-full px-5">
            Meet the creator
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function GeographyStyleCard({
  slug,
  label,
  scope,
  summary,
  typography,
  imagery,
  uiRules,
  sources,
}: (typeof geographyStyleDirections)[number]) {
  const theme = getGeographyTheme(slug);
  const palette = [
    { label: 'Primary', value: theme.colors.primary },
    { label: 'Accent', value: theme.colors.accent },
    { label: 'Background', value: theme.colors.background },
    { label: 'Border', value: theme.colors.border },
  ];

  return (
    <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
      <CardHeader className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">{scope}</Badge>
          <Badge variant="outline" className="rounded-full px-3 py-1">
            {label}
          </Badge>
        </div>
        <div>
          <CardTitle className="text-2xl">{label}</CardTitle>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">{summary}</p>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Palette
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {palette.map(item => (
              <div
                key={item.label}
                className="rounded-[1rem] border border-border/70 bg-background/70 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.value}</p>
                  </div>
                  <div
                    className="h-10 w-10 rounded-full border border-white/50 shadow-inner"
                    style={{ background: item.value }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Typography
            </p>
            <p className="text-sm leading-6 text-muted-foreground">{typography}</p>
            <p className="text-xs text-muted-foreground">
              Theme stack:{' '}
              <span className="font-medium text-foreground">{theme.typescale.headingStack}</span>
            </p>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Implementation notes
            </p>
            <div className="space-y-2 text-sm leading-6 text-muted-foreground">
              {uiRules.map(item => (
                <p key={item}>{item}</p>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Image direction
            </p>
            <div className="space-y-2 text-sm leading-6 text-muted-foreground">
              {imagery.map(item => (
                <p key={item}>{item}</p>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Research sources
            </p>
            {sources.length > 0 ? (
              <div className="space-y-2 text-sm leading-6 text-muted-foreground">
                {sources.map(source => (
                  <p key={source.href}>
                    <a
                      href={source.href}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {source.label}
                    </a>
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm leading-6 text-muted-foreground">
                Region-level styling is a Kalovate synthesis that bridges the two Monroe County
                localities rather than reproducing an official county brand.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function StyleGuide() {
  return (
    <div className="min-h-[100dvh] bg-background text-foreground">
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell py-16">
          <p className="editorial-kicker">Phase 4A Reference</p>
          <h1 className="section-title mt-4 max-w-4xl">
            Kalovate’s public product should feel local, tactile, and designed for a phone before it
            ever feels like a marketplace.
          </h1>
          <p className="section-copy mt-6 max-w-3xl">
            This route is the canonical visual and product reference for the redesign. Shell
            behavior, navigation roles, browse-state ownership, motion, and fallback behavior are
            defined here before they are distributed across implementation.
          </p>
        </div>
      </section>

      <main className="page-shell py-12">
        <Tabs defaultValue="tokens" className="space-y-10">
          <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-[1.25rem] bg-muted/80 p-2">
            <TabsTrigger value="tokens" className="rounded-full px-4">
              Tokens
            </TabsTrigger>
            <TabsTrigger value="components" className="rounded-full px-4">
              Components
            </TabsTrigger>
            <TabsTrigger value="geography" className="rounded-full px-4">
              Geography
            </TabsTrigger>
            <TabsTrigger value="routing" className="rounded-full px-4">
              Routing
            </TabsTrigger>
            <TabsTrigger value="behavior" className="rounded-full px-4">
              Behavior
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tokens" className="space-y-12">
            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Color system</p>
                <h2 className="mt-3 text-3xl font-semibold">Warm editorial palette</h2>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {colorTokens.map(token => (
                  <TokenSwatch key={token.label} {...token} />
                ))}
              </div>
            </section>

            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Typography</p>
                <h2 className="mt-3 text-3xl font-semibold">Display with restraint</h2>
              </div>
              <div className="grid gap-4">
                {typeScale.map(item => (
                  <Card key={item.label} className="rounded-[1.5rem] border-border/70 shadow-sm">
                    <CardContent className="space-y-3 p-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        {item.label}
                      </p>
                      <p className={item.className}>{item.sample}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Spacing and layout</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <p>`--page-max-width`: 80rem</p>
                  <p>`--page-narrow-width`: 46rem</p>
                  <p>`--section-padding-y`: responsive 3.5rem → 6rem</p>
                  <p>Phone gutters default to 1rem; larger breakpoints expand from there.</p>
                  <p>All primary interactive targets must meet the 44×44 minimum.</p>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Motion</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                  {motionPrinciples.map(item => (
                    <p key={item}>{item}</p>
                  ))}
                </CardContent>
              </Card>
            </section>
          </TabsContent>

          <TabsContent value="components" className="space-y-12">
            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Controls</p>
                <h2 className="mt-3 text-3xl font-semibold">Buttons, inputs, and states</h2>
              </div>
              <div className="grid gap-6 lg:grid-cols-2">
                <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <div className="flex flex-wrap gap-3">
                      <Button className="touch-target rounded-full px-5">Primary action</Button>
                      <Button variant="outline" className="touch-target rounded-full px-5">
                        Secondary action
                      </Button>
                      <Button variant="ghost" className="touch-target rounded-full px-5">
                        Quiet action
                      </Button>
                    </div>
                    <Input
                      placeholder="Search classes, creators, or neighborhoods"
                      className="touch-target rounded-full"
                    />
                  </CardContent>
                </Card>
                <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                  <CardContent className="space-y-4 p-6">
                    <div className="flex flex-wrap gap-2">
                      <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                        Primary badge
                      </Badge>
                      <Badge variant="secondary" className="rounded-full px-3 py-1">
                        Secondary badge
                      </Badge>
                      <Badge variant="outline" className="rounded-full px-3 py-1">
                        Filter chip
                      </Badge>
                    </div>
                    <div className="glass-panel rounded-[1.5rem] p-5">
                      <p className="text-sm font-medium">Sheets and drawers</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        Mobile filter controls open as overlays, not inline layout shifts.
                        Confirmation and booking affordances stay anchored near the bottom edge.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Cards</p>
                <h2 className="mt-3 text-3xl font-semibold">
                  Discovery surfaces scale by viewport
                </h2>
              </div>
              <SampleCards />
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Touch targets and safe area</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-6 text-muted-foreground">
                  <p>Interactive controls use `.touch-target` with a 44×44 minimum hit area.</p>
                  <p>
                    Bottom nav, checkout actions, and sticky bars account for
                    `env(safe-area-inset-bottom)`.
                  </p>
                  <p>
                    Full-height surfaces should use `dvh` or `svh`, not legacy `vh` assumptions.
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Brand notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                  {brandNotes.map(note => (
                    <p key={note}>{note}</p>
                  ))}
                </CardContent>
              </Card>
            </section>
          </TabsContent>

          <TabsContent value="geography" className="space-y-12">
            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Region and locality systems</p>
                <h2 className="mt-3 text-3xl font-semibold">
                  Monroe County needs one umbrella style and two distinct locality voices
                </h2>
              </div>
              <div className="grid gap-4">
                {geographyStyleDirections.map(direction => (
                  <GeographyStyleCard key={direction.slug} {...direction} />
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Geography hierarchy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                  <p>
                    Monroe County should stay broad and neutral when the whole region is selected.
                    It should never look like “Bloomington by default.”
                  </p>
                  <p>
                    Bloomington gets the most institutional energy: crimson, cream, limestone, and
                    tighter structure pulled from IU’s digital identity and campus character.
                  </p>
                  <p>
                    Ellettsville gets the most handcrafted energy: heritage stone, greenway sage,
                    historic downtown warmth, and softer community-scale surfaces.
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Use this in implementation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm leading-6 text-muted-foreground">
                  <p>
                    Region-level headers, chips, and filter shells should use the Monroe County
                    token set when no locality is selected.
                  </p>
                  <p>
                    Locality pages can safely lean further into their own palette, typography, and
                    image direction without losing the shared Kalovate shell.
                  </p>
                  <p>
                    Creative work should reference these locality cards before inventing a new color
                    story or generic hero treatment.
                  </p>
                </CardContent>
              </Card>
            </section>
          </TabsContent>

          <TabsContent value="routing" className="space-y-12">
            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Shell assignment</p>
                <h2 className="mt-3 text-3xl font-semibold">Public route map</h2>
              </div>
              <div className="overflow-hidden rounded-[1.5rem] border border-border/70 bg-card shadow-sm">
                <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_minmax(0,0.9fr)_minmax(0,0.8fr)] gap-4 border-b border-border/70 px-5 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  <span>Route</span>
                  <span>Shell</span>
                  <span>Header</span>
                  <span>Bottom nav</span>
                  <span>Sticky CTA</span>
                </div>
                {PUBLIC_ROUTE_MAP.map(route => (
                  <div
                    key={route.routeKey}
                    className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_minmax(0,0.9fr)_minmax(0,0.8fr)] gap-4 border-b border-border/50 px-5 py-4 text-sm last:border-b-0"
                  >
                    <div>
                      <p className="font-medium text-foreground">{route.pathLabel}</p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">
                        {route.description}
                      </p>
                    </div>
                    <span>{route.shell}</span>
                    <span>{route.headerMode}</span>
                    <span>{route.bottomNavVisible ? 'visible' : 'hidden'}</span>
                    <span>{route.stickyCta}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Navigation model</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div>
                    <p className="font-semibold text-foreground">Guest bottom nav</p>
                    <p className="mt-2 text-muted-foreground">
                      {PUBLIC_BOTTOM_NAV.guest.map(item => item.label).join(' | ')}
                    </p>
                  </div>
                  <Separator />
                  <div>
                    <p className="font-semibold text-foreground">Authenticated bottom nav</p>
                    <p className="mt-2 text-muted-foreground">
                      {PUBLIC_BOTTOM_NAV.authenticated.map(item => item.label).join(' | ')}
                    </p>
                  </div>
                  <Separator />
                  <div className="space-y-2 text-muted-foreground">
                    <p>
                      <span className="font-semibold text-foreground">Desktop primary:</span>{' '}
                      {DESKTOP_NAV_MODEL.primary.join(', ')}
                    </p>
                    <p>
                      <span className="font-semibold text-foreground">Desktop utility:</span>{' '}
                      {DESKTOP_NAV_MODEL.utility.join(', ')}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Root and geography behavior</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-6 text-muted-foreground">
                  <p>{ROOT_ROUTE_DISCOVERY_BEHAVIOR}</p>
                  <p>{GEOGRAPHY_SWITCHER_BEHAVIOR}</p>
                </CardContent>
              </Card>
            </section>
          </TabsContent>

          <TabsContent value="behavior" className="space-y-12">
            <section className="space-y-5">
              <div>
                <p className="editorial-kicker">Browse-state ownership</p>
                <h2 className="mt-3 text-3xl font-semibold">What belongs in the URL</h2>
              </div>
              <div className="grid gap-4">
                {BROWSE_STATE_OWNERSHIP.map(item => (
                  <Card key={item.param} className="rounded-[1.5rem] border-border/70 shadow-sm">
                    <CardContent className="flex flex-col gap-2 p-5 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="font-semibold text-foreground">{item.param}</p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.notes}</p>
                      </div>
                      <Badge variant="outline" className="w-fit rounded-full px-3 py-1">
                        {item.owner}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Low-inventory fallback matrix</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-6 text-muted-foreground">
                  {GEOGRAPHY_FALLBACK_MATRIX.map(item => (
                    <div key={item.scenario}>
                      <p className="font-semibold text-foreground">{item.scenario}</p>
                      <p>{item.fallback}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-border/70 shadow-sm">
                <CardHeader>
                  <CardTitle>Budgets and support matrix</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-6 text-muted-foreground">
                  <div>
                    <p className="font-semibold text-foreground">Performance</p>
                    {PERFORMANCE_BUDGETS.map(item => (
                      <p key={item}>{item}</p>
                    ))}
                  </div>
                  <Separator />
                  <div>
                    <p className="font-semibold text-foreground">Manual QA targets</p>
                    {BROWSER_SUPPORT_MATRIX.map(item => (
                      <p key={item}>{item}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
